import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { STRIPE_TEST_MODE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { setTitle, setMeta, setCanonical } from "@/lib/seo";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";
import {
  Check, Loader2, Search, Bot, PhoneCall, Headphones, Megaphone, Sparkles,
  Mail, ArrowRight, Settings2, ChevronDown,
} from "lucide-react";

const PLANS = [
  {
    key: "listing", name: "Listing", price: 29, tagline: "Get found and capture leads.",
    features: [
      "Directory listing on savingamericamoney.com built for visibility on Google and AI search",
      "Lead dashboard with intake forms and lead tracking",
    ],
  },
  {
    key: "growth", name: "Growth", price: 97, tagline: "Answer every lead, automatically.",
    features: [
      "Everything in Listing",
      "Local phone number with call tracking",
      "AI chatbot and AI text replies",
      "Google Business Profile setup",
      "Review requests",
      "Image ads",
    ],
  },
  {
    key: "pro", name: "Pro", price: 297, popular: true, tagline: "A 24/7 AI team running your front desk.",
    features: [
      "Everything in Growth",
      "AI receptionist that answers calls 24/7 and books appointments",
      "Video and motion ads",
      "Facebook and LinkedIn ad campaigns run with our AI brain",
      "Automated follow-up sequences",
    ],
  },
  {
    key: "max", name: "Max", price: 497, tagline: "Add an AI sales caller and full reputation management.",
    features: [
      "Everything in Pro",
      "AI sales caller that works down call lists and transfers interested leads live to a human salesperson",
      "Full reputation management",
      "A landing page",
      "Monthly strategy call",
    ],
  },
];

const STEPS = [
  { Icon: Search, title: "Get listed in the directory", text: "Your business goes live on savingamericamoney.com, built to be found on Google and AI search." },
  { Icon: Settings2, title: "Your dashboard is set up automatically", text: "After checkout we provision your marketing dashboard and send a login invite to your email." },
  { Icon: Megaphone, title: "Upgrade anytime", text: "Add ads, AI calling, a receptionist and more whenever you're ready. No long-term contracts." },
];

const COMPARE = [
  { label: "Directory listing + lead dashboard", listing: true, growth: true, pro: true, max: true },
  { label: "Local number, call tracking & AI texting", listing: false, growth: true, pro: true, max: true },
  { label: "Google Business Profile + review requests", listing: false, growth: true, pro: true, max: true },
  { label: "AI receptionist (answers & books 24/7)", listing: false, growth: false, pro: true, max: true },
  { label: "Video/motion ads + Facebook & LinkedIn campaigns", listing: false, growth: false, pro: true, max: true },
  { label: "AI sales caller + live transfer to a human", listing: false, growth: false, pro: false, max: true },
  { label: "Reputation management + landing page + strategy call", listing: false, growth: false, pro: false, max: true },
];

const FAQS = [
  { q: "How much does it cost?", a: "Plans start at $29/mo for the Listing tier and go up to $497/mo for Max. Every plan is billed monthly." },
  { q: "Are there long-term contracts?", a: "No. Every plan is month-to-month. Upgrade, downgrade or cancel anytime from the manage-billing page." },
  { q: "I'm too busy to run marketing — can you do it for me?", a: "That's the point. Pro and Max use our AI to answer calls, reply to leads, and run ad campaigns so you don't have to." },
  { q: "Is ad spend included?", a: "No. Facebook and LinkedIn ad spend is paid separately, directly to those platforms. Pro and Max include monthly usage allowances for AI calling and texting." },
];

const CATEGORIES = ["Home Services", "HVAC", "Roofing", "Plumbing", "Electrical", "Landscaping", "Restoration", "Real Estate", "Auto", "Health & Dental", "Professional Services", "Restaurant & Retail", "Other"];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border py-4" data-testid="plans-faq-item">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 text-left">
        <span className="font-semibold text-sam-navy">{q}</span>
        <ChevronDown className={`h-5 w-5 text-sam-slate shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-sm text-sam-slate leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Plans() {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", category: "", city: "", zip: "" });
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    const title = "Plans & Pricing | Saving America Money";
    const desc = "Get found on Google and AI search, capture every lead, and answer every call with AI. Simple monthly plans from $29, no long-term contracts.";
    setTitle(title);
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setCanonical(`${window.location.origin}/plans`);
  }, []);

  const openCheckout = (planKey) => {
    setSelected(planKey);
    setForm((f) => ({ ...f, category: f.category || "" }));
  };

  const submit = async () => {
    if (!form.business_name.trim() || !form.email.trim()) return toast.error("Business name and email are required");
    if (!agree) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/plans/checkout", { plan: selected, ...form, origin_url: window.location.origin, affiliate_id: window.affiliateId || undefined, terms_accepted: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const selPlan = PLANS.find((p) => p.key === selected);

  return (
    <div data-testid="plans-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sam-amber"><Sparkles className="h-4 w-4" />Marketing plans</span>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            Get found. Get calls. Run it all from one place.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-sam-cream/75 max-w-2xl">
            Saving America Money helps local businesses get found on Google and AI search, capture every lead,
            and answer every call with AI, all from one simple dashboard.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="bg-sam-amber text-sam-navy hover:bg-sam-amber/90 font-bold h-12 px-7 text-base" data-testid="hero-see-plans">
              <a href="#pricing">See Plans <ArrowRight className="h-4 w-4 ml-2" /></a>
            </Button>
            <Button variant="outline" className="border-sam-cream/30 bg-white/5 text-sam-cream hover:bg-white/10 hover:text-sam-cream h-12 px-6 font-semibold" onClick={() => setManageOpen(true)} data-testid="manage-billing-btn">
              Manage my billing
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <h2 className="font-display font-black text-3xl text-sam-navy text-center">How it works</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-7" data-testid={`how-step-${i}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-sam-navy/10 grid place-items-center"><s.Icon className="h-5 w-5 text-sam-navy" /></div>
                <span className="font-display font-black text-2xl text-sam-crimson/30">{i + 1}</span>
              </div>
              <h3 className="mt-4 font-display font-bold text-sam-navy text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-sam-slate leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-4 sm:px-6 pb-6 scroll-mt-20">
        <div className="text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-sam-navy">Simple monthly plans</h2>
          <p className="mt-3 text-sam-slate">No long-term contracts. Upgrade or downgrade anytime.</p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 items-start">
          {PLANS.map((p) => (
            <div key={p.key}
              data-testid={`plan-${p.key}`}
              className={`relative rounded-2xl bg-card p-6 flex flex-col border-2 ${p.popular ? "border-sam-crimson shadow-[0_18px_50px_-20px_rgba(185,28,28,0.5)] lg:-mt-3 lg:mb-3" : "border-sam-navy/15"}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sam-crimson text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1" data-testid="plan-pro-badge">Most Popular</div>}
              <div className="text-xs font-bold uppercase tracking-widest text-sam-slate">{p.name}</div>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-display text-4xl font-black text-sam-navy">${p.price}</span>
                <span className="text-sam-slate mb-1.5 text-sm">/mo</span>
              </div>
              <p className="mt-2 text-sm text-sam-navy/80 font-medium min-h-[40px]">{p.tagline}</p>
              <ul className="mt-5 space-y-2.5 text-sm text-sam-navy flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-sam-green shrink-0 mt-0.5" /><span>{f}</span></li>
                ))}
              </ul>
              <Button
                onClick={() => openCheckout(p.key)}
                data-testid={`plan-${p.key}-cta`}
                className={`mt-6 w-full font-semibold ${p.popular ? "bg-sam-crimson hover:bg-sam-crimson/90" : "bg-sam-navy hover:bg-sam-navy2"}`}>
                Get {p.name}
              </Button>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-sam-slate text-center max-w-3xl mx-auto" data-testid="plans-fine-print">
          Ad spend on Facebook and LinkedIn is paid separately. Pro and Max include monthly usage allowances.
          Full websites and portals quoted separately.
        </p>
      </section>

      {/* Comparison grid */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-14">
        <h2 className="font-display font-black text-2xl sm:text-3xl text-sam-navy text-center mb-8">Compare what's included</h2>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm min-w-[640px]" data-testid="plans-compare-table">
            <thead>
              <tr className="bg-sam-navy text-sam-cream">
                <th className="text-left font-semibold px-4 py-3">Feature</th>
                {PLANS.map((p) => <th key={p.key} className="px-4 py-3 font-bold">{p.name}<div className="text-[11px] font-normal text-sam-cream/60">${p.price}/mo</div></th>)}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => (
                <tr key={row.label} className={i % 2 ? "bg-sam-cream/40" : "bg-card"}>
                  <td className="px-4 py-3 text-sam-navy">{row.label}</td>
                  {["listing", "growth", "pro", "max"].map((k) => (
                    <td key={k} className="px-4 py-3 text-center">
                      {row[k] ? <Check className="h-4 w-4 text-sam-green mx-auto" /> : <span className="text-sam-slate/30">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* AI value strip */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-6">
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { Icon: Bot, title: "AI that answers", text: "Chat and text replies handle leads the moment they arrive." },
            { Icon: Headphones, title: "AI receptionist", text: "Answers calls 24/7 and books appointments on your calendar." },
            { Icon: PhoneCall, title: "AI sales caller", text: "Works your call lists and transfers hot leads to a real person." },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-border bg-sam-cream/40 p-6">
              <c.Icon className="h-6 w-6 text-sam-crimson" />
              <h3 className="mt-3 font-display font-bold text-sam-navy">{c.title}</h3>
              <p className="mt-1.5 text-sm text-sam-slate">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
        <h2 className="font-display font-black text-2xl sm:text-3xl text-sam-navy text-center mb-6">Questions, answered</h2>
        <div>{FAQS.map((f) => <FaqItem key={f.q} {...f} />)}</div>
      </section>

      {/* Postcard mailer cross-link */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16">
        <div className="rounded-3xl bg-sam-navy text-sam-cream p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between" data-testid="plans-mailer-cta">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Mail className="h-4 w-4" />Premium Postcard Mailer</span>
            <h3 className="mt-2 font-display font-black text-2xl sm:text-3xl">Want to hit 10,000 homes per route?</h3>
            <p className="mt-2 text-sam-cream/75 max-w-xl text-sm">Share our premium 9×12 postcard mailer with other local businesses. A bold, printed way to get seen alongside your online plan.</p>
          </div>
          <Button asChild className="bg-sam-amber text-sam-navy hover:bg-sam-amber/90 font-bold h-12 px-7 shrink-0" data-testid="plans-to-mailer">
            <Link to="/mailer">Explore the mailer <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 pb-20 text-center">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-sam-navy">Ready when you are.</h2>
        <p className="mt-3 text-sam-slate">Start with Listing or jump straight to Pro. Change plans anytime.</p>
        <Button asChild className="mt-6 bg-sam-crimson hover:bg-sam-crimson/90 h-12 px-8 font-bold" data-testid="final-cta">
          <a href="#pricing">See Plans</a>
        </Button>
      </section>

      {/* Checkout dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setAgree(false); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="plan-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{selPlan && `Start the ${selPlan.name} plan — $${selPlan.price}/mo`}</DialogTitle>
            <DialogDescription>Tell us about your business so we can set up your dashboard right after checkout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Business name *</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Acme Plumbing" data-testid="co-business" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Your name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="co-contact" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="co-phone" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@business.com" data-testid="co-email" /></div>
            <div>
              <Label>Industry / category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="co-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="co-city" /></div>
              <div><Label>ZIP</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="co-zip" /></div>
            </div>
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90 mt-1" onClick={submit} disabled={busy || !agree} data-testid="co-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Continue to secure checkout
            </Button>
            <TermsAgree checked={agree} onChange={setAgree} testid="plan-checkout-terms" />
            <p className="text-xs text-center text-muted-foreground">Billed monthly. Cancel anytime.{STRIPE_TEST_MODE ? " Test card 4242 4242 4242 4242." : ""}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage billing / cancel dialog */}
      <ManageBillingDialog open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  );
}

function ManageBillingDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("email"); // email | offer | done
  const [offer, setOffer] = useState(null);

  const openPortal = async () => {
    if (!email.trim()) return toast.error("Enter your account email");
    setBusy(true);
    try {
      const { data } = await api.post("/plans/portal", { email, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "No subscription found"); setBusy(false); }
  };

  const startCancel = async () => {
    if (!email.trim()) return toast.error("Enter your account email");
    setBusy(true);
    try {
      const { data } = await api.post("/plans/cancel/offer", { email });
      setOffer(data); setStep("offer");
    } catch (e) { toast.error(e?.response?.data?.detail || "No subscription found"); }
    finally { setBusy(false); }
  };

  const keepListing = async () => {
    setBusy(true);
    try { await api.post("/plans/cancel/downgrade", { email }); setStep("done"); toast.success("Switched to the $29/mo Listing plan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not switch"); }
    finally { setBusy(false); }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try { await api.post("/plans/cancel/confirm", { email }); setStep("done"); toast.success("Your plan will end — no further charges"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep("email"); setOffer(null); setEmail(""); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent data-testid="manage-billing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Manage your billing</DialogTitle>
          <DialogDescription>Use the email on your subscription to update payment, change plans, or cancel.</DialogDescription>
        </DialogHeader>
        {step === "email" && (
          <div className="space-y-3">
            <div><Label>Account email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" data-testid="manage-email" /></div>
            <Button className="w-full bg-sam-navy hover:bg-sam-navy2" onClick={openPortal} disabled={busy} data-testid="manage-portal-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Open billing & change plan
            </Button>
            <button onClick={startCancel} disabled={busy} className="w-full text-sm text-sam-slate hover:text-sam-crimson underline" data-testid="manage-cancel-link">I want to cancel</button>
          </div>
        )}
        {step === "offer" && (
          <div className="space-y-4" data-testid="cancel-offer">
            {offer?.already_listing ? (
              <p className="text-sm text-sam-navy">You're on the Listing plan. Cancelling will stop all charges after your current period.</p>
            ) : (
              <div className="rounded-xl border-2 border-sam-amber/50 bg-sam-amber/10 p-4">
                <p className="font-semibold text-sam-navy">Keep your directory listing and dashboard for $29/mo instead?</p>
                <p className="mt-1 text-sm text-sam-slate">Stay searchable and keep your leads flowing — you can upgrade again anytime.</p>
                <Button className="mt-3 w-full bg-sam-green hover:bg-sam-green/90" onClick={keepListing} disabled={busy} data-testid="cancel-keep-listing">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Yes, switch me to $29/mo
                </Button>
              </div>
            )}
            <Button variant="outline" className="w-full border-sam-crimson/40 text-sam-crimson hover:bg-sam-crimson/5" onClick={confirmCancel} disabled={busy} data-testid="cancel-confirm">
              No thanks, cancel my plan
            </Button>
          </div>
        )}
        {step === "done" && (
          <div className="py-4 text-center" data-testid="manage-done">
            <Check className="h-10 w-10 text-sam-green mx-auto" />
            <p className="mt-3 text-sam-navy font-semibold">All set.</p>
            <Button className="mt-4 bg-sam-navy hover:bg-sam-navy2" onClick={reset}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
